import type { DecryptedBlock } from "@/context/AppContext";

export type SchemaField = {
  name: string;
  key: string;
  type: string;
  cellEditor?: string;
  colWidth?: number;
  options?: string[];
  importAliases?: string[];
  autofillCurrentDate?: boolean;
  /** 1 shares a form line (at most two fields). Omitted or 2 is a full line. */
  formSpan?: 1 | 2;
};

export type TableViewSort = {
  sort: "asc" | "desc" | null;
  sortIndex: number | null;
};

export type TableViewQuickAdd = {
  extractRegex?: string;
};

export type TableViewConfig = {
  columns?: string[];
  editFields?: string[];
  filters?: Record<string, unknown>;
  sorts?: Record<string, TableViewSort>;
  quickAdd?: TableViewQuickAdd;
};

export type TableView = TableViewConfig & {
  key: string;
  name: string;
};

export type TableDefaultView = TableViewConfig & {
  name?: string;
};

export type TablePayload = {
  name?: string;
  isDefault?: boolean;
  schema: SchemaField[];
  defaultView?: TableDefaultView;
  views?: TableView[];
};

type BlockPayload = {
  parent?: string;
};

/** Matches `markdown`, `mark down`, `mark_down`, etc. */
export function isMarkdownSchemaType(type: string): boolean {
  return type.replace(/\s+/g, "").replace(/_/g, "").toLowerCase() === "markdown";
}

/** Matches `html`, `HTML`, etc. */
export function isHtmlSchemaType(type: string): boolean {
  return type.replace(/\s+/g, "").replace(/_/g, "").toLowerCase() === "html";
}

export function isRecord(v: unknown): v is Record<string, unknown> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  return Object.keys(v).length > 0;
}

/** Parses boolean values, including string `"true"` / `"false"`. */
export function parseBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return undefined;
}

export function getBlockPayload(raw: unknown): BlockPayload | null {
  if (!isRecord(raw)) return null;
  const parent = raw.parent;
  if (parent !== undefined && typeof parent !== "string") return null;
  return { parent };
}

export function getTablePayload(raw: unknown): TablePayload | null {
  if (!isRecord(raw)) return null;
  const name = raw.name;
  const isDefault = raw.isDefault;
  const schemaRaw = raw.schema;
  const schema: SchemaField[] = [];
  if (Array.isArray(schemaRaw)) {
    for (const item of schemaRaw) {
      if (!isRecord(item)) continue;
      const key = item.key;
      const fieldName = item.name;
      const type = item.type;
      if (typeof key !== "string" || typeof fieldName !== "string" || typeof type !== "string") {
        continue;
      }
      const cellEditor =
        typeof item.cellEditor === "string" ? item.cellEditor : undefined;
      const colWidth =
        typeof item.colWidth === "number" ? item.colWidth : undefined;
      let options: string[] | undefined;
      if (Array.isArray(item.options)) {
        options = item.options.filter((o): o is string => typeof o === "string");
      }
      let importAliases: string[] | undefined;
      if (Array.isArray(item.importAliases)) {
        importAliases = item.importAliases.filter(
          (a): a is string => typeof a === "string",
        );
      }
      const autofillCurrentDate =
        parseBoolean(item.autofillCurrentDate) === true ? true : undefined;
      const formSpan =
        item.formSpan === 1 || item.formSpan === 2 ? item.formSpan : undefined;
      schema.push({
        name: fieldName,
        key,
        type,
        cellEditor,
        colWidth,
        options,
        importAliases,
        autofillCurrentDate,
        formSpan,
      });
    }
  }
  const parseViewConfig = (item: Record<string, unknown>): TableViewConfig => {
    let columns: string[] | undefined;
    if (Array.isArray(item.columns)) {
      columns = item.columns.filter((c): c is string => typeof c === "string");
    }
    let editFields: string[] | undefined;
    if (Array.isArray(item.editFields)) {
      editFields = item.editFields.filter((c): c is string => typeof c === "string");
    }
    let filters: Record<string, unknown> | undefined;
    if (isRecord(item.filters)) {
      filters = item.filters;
    }
    let sorts: Record<string, TableViewSort> | undefined;
    if (isRecord(item.sorts)) {
      sorts = {};
      for (const [sortKey, sortVal] of Object.entries(item.sorts)) {
        if (!isRecord(sortVal)) continue;
        const sort = sortVal.sort;
        const sortIndex = sortVal.sortIndex;
        sorts[sortKey] = {
          sort: sort === "asc" || sort === "desc" ? sort : null,
          sortIndex: typeof sortIndex === "number" ? sortIndex : null,
        };
      }
    }
    let quickAdd: TableViewQuickAdd | undefined;
    if (isRecord(item.quickAdd)) {
      const extractRegex = item.quickAdd.extractRegex;
      if (typeof extractRegex === "string" && extractRegex.trim()) {
        quickAdd = { extractRegex };
      }
    }
    return { columns, editFields, filters, sorts, quickAdd };
  };

  let defaultView: TableDefaultView | undefined;
  const defaultViewRaw = raw.defaultView;
  if (isRecord(defaultViewRaw)) {
    const defaultViewName = defaultViewRaw.name;
    defaultView = {
      ...parseViewConfig(defaultViewRaw),
      name: typeof defaultViewName === "string" ? defaultViewName : undefined,
    };
  }

  let views: TableView[] | undefined;
  const viewsRaw = raw.views;
  if (Array.isArray(viewsRaw)) {
    const parsed: TableView[] = [];
    for (const item of viewsRaw) {
      if (!isRecord(item)) continue;
      const key = item.key;
      const viewName = item.name;
      if (typeof key !== "string" || typeof viewName !== "string") continue;
      parsed.push({ key, name: viewName, ...parseViewConfig(item) });
    }
    if (parsed.length > 0) views = parsed;
  }

  return {
    name: typeof name === "string" ? name : undefined,
    isDefault: parseBoolean(isDefault),
    schema,
    defaultView,
    views,
  };
}

export function resolveTableView({
  viewKey,
  defaultView,
  views,
}: {
  viewKey: string | null;
  defaultView: TableDefaultView | undefined;
  views: TableView[];
}): TableViewConfig | null {
  if (viewKey === null) {
    return defaultView ?? null;
  }
  return views.find((view) => view.key === viewKey) ?? null;
}

export function resolveEditFieldKeys({
  view,
  schema,
}: {
  view: TableViewConfig | null | undefined;
  schema: SchemaField[];
}): string[] {
  if (view?.editFields?.length) {
    return view.editFields;
  }
  if (view?.columns?.length) {
    return view.columns;
  }
  return schema.map((f) => f.key);
}

/** Groups fields into rows of at most two. Consecutive half-width fields share a row. */
export function packFormRows<T extends { formSpan?: 1 | 2 }>({
  fields,
}: {
  fields: T[];
}): T[][] {
  const rows: T[][] = [];
  let index = 0;
  while (index < fields.length) {
    const field = fields[index];
    if (!field) break;
    const next = fields[index + 1];
    if (field.formSpan === 1 && next?.formSpan === 1) {
      rows.push([field, next]);
      index += 2;
      continue;
    }
    rows.push([field]);
    index += 1;
  }
  return rows;
}

export function resolveEditSchemaFields({
  view,
  schema,
}: {
  view: TableViewConfig | null | undefined;
  schema: SchemaField[];
}): SchemaField[] {
  const keys = resolveEditFieldKeys({ view, schema });
  const byKey = new Map(schema.map((field) => [field.key, field]));
  return keys
    .map((key) => byKey.get(key))
    .filter((field): field is SchemaField => field !== undefined);
}

export function getPayloadDataRecord(
  block: DecryptedBlock,
): Record<string, unknown> | null {
  const raw = block.data;
  if (!isRecord(raw)) {
    return null;
  }
  return raw as Record<string, unknown>;
}

export type GridCell = { value?: unknown; data?: unknown };

export function normalizeToCell(v: unknown): GridCell {
  if (isRecord(v) && ("value" in v || "data" in v)) {
    return {
      value: v.value,
      data: "data" in v ? v.data : undefined,
    };
  }
  return { value: v };
}

function rawToGridCell(raw: unknown): GridCell | null {
  if (
    typeof raw === "object" &&
    raw !== null &&
    !Array.isArray(raw) &&
    Object.keys(raw).length === 0
  ) {
    return null;
  }
  return normalizeToCell(raw);
}

export function getGridCell(
  block: DecryptedBlock | undefined,
  key: string,
): GridCell | null | undefined {
  if (!block) return undefined;
  const nested = getRowDataRecord(block);
  if (nested && key in nested) {
    return rawToGridCell(nested[key]);
  }
  const top = getPayloadDataRecord(block);
  if (!top || !(key in top) || key === "data") return undefined;
  return rawToGridCell(top[key]);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function getRowDataRecord(block: DecryptedBlock): Record<string, unknown> | null {
  const payload = getPayloadDataRecord(block);
  if (!payload) return null;
  const data = payload.data;
  if (!isPlainObject(data)) return null;
  return data;
}

/** Local calendar date as `YYYY-MM-DD`. */
export function localISODate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Initial row `data` with schema autofill values applied. */
export function buildAutofillRowData({
  schema,
}: {
  schema: SchemaField[];
}): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  const today = localISODate();
  for (const field of schema) {
    if (field.type === "date" && field.autofillCurrentDate) {
      data[field.key] = { value: today };
    }
  }
  return data;
}

export function ensureRowDataRecord(block: DecryptedBlock): Record<string, unknown> | null {
  const payload = getPayloadDataRecord(block);
  if (!payload) return null;
  if (!isPlainObject(payload.data)) {
    payload.data = {};
  }
  return payload.data;
}

export function matchingBlocksForTable({
  blocks,
  tableId,
  viewedIsDefault,
}: {
  blocks: DecryptedBlock[];
  tableId: string;
  viewedIsDefault: boolean;
}): DecryptedBlock[] {
  return blocks
    .filter((b) => {
      if (b.type === "table") return false;
      const data = getBlockPayload(b.data);
      if (!data) return false;
      const parent = data.parent;

      if (!viewedIsDefault) {
        return parent === tableId;
      }
      return !parent || parent === tableId;
    })
    .sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

export function rowBelongsToTable({
  rowBlock,
  tableId,
  viewedIsDefault,
}: {
  rowBlock: DecryptedBlock;
  tableId: string;
  viewedIsDefault: boolean;
}): boolean {
  const data = getBlockPayload(rowBlock.data);
  if (!data) return false;
  if (!viewedIsDefault) {
    return data.parent === tableId;
  }
  return !data.parent || data.parent === tableId;
}

const selectTagChipClassName =
  "shrink-0 rounded-[2px] border border-[#5c5c5c] bg-[#3d3d3d] px-1 py-0 text-xs leading-none";

const selectTagEmojiOnlyClassName = "shrink-0 text-xs leading-none";

export function selectOptionDisplayClassName(option: string): string {
  return isSingleEmojiOption(option)
    ? selectTagEmojiOnlyClassName
    : selectTagChipClassName;
}

export function isSingleEmojiOption(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  try {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    const segments = [...segmenter.segment(trimmed)];
    if (segments.length !== 1) return false;
    return /\p{Extended_Pictographic}/u.test(segments[0]!.segment);
  } catch {
    return false;
  }
}
